import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../context/AppContext";
import {
  Modal,
  DashboardStatCard,
  Pagination,
  AttachmentPreviewRow,
} from "./UI";
import { scoreColor, scoreBg, scoreLabel } from "./gapAssessmentTheme";
import { ArcGauge, ScoreRing } from "./ArcGauge";
import {
  AlertTriangle,
  ClipboardList,
  Calendar,
  ChevronUp,
  ChevronDown,
  Search,
  Eye,
  ArrowLeft,
  X as XIcon,
  Building2,
  BarChart3,
  Users,
  ShieldCheck,
  ShieldAlert,
  FileText,
  FileDown,
  FileSpreadsheet,
  History,
  Loader2,
  User,
  CloudUpload,
  Link2,
} from "lucide-react";
import {
  exportSubmissionPdf,
  exportSubmissionExcel,
  safeExportFilename,
} from "../../utils/exportSubmissionPdf";
import { tprmAttachmentPreviewPath } from "./gapRecommendationUtils";
import { showError, showInfo } from "../../utils/sweetAlert";
import { useDepartments } from "../../hooks/useDepartments";

const PIA_RISK_COLORS = {
  "Very Low": "#10b981",
  Low: "#10b981",
  Medium: "#f59e0b",
  High: "#ef4444",
  Critical: "#7c3aed",
};

function piaRiskColor(level) {
  if (!level) return "#64748b";
  const key = String(level).trim();
  if (PIA_RISK_COLORS[key]) return PIA_RISK_COLORS[key];
  const normalized = key.toLowerCase();
  if (normalized.includes("critical") || normalized.includes("very high")) {
    return "#7c3aed";
  }
  if (normalized.includes("high")) return "#ef4444";
  if (normalized.includes("medium")) return "#f59e0b";
  if (normalized.includes("low")) return "#10b981";
  return "#64748b";
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ROPA_ACTIVITY_FIELD_LABELS = {
  "activity.dataOwner": "Data Owner",
  "activity.purpose": "Purpose",
  "activity.name": "Activity Name",
  "activity.dept": "Activity Department",
  "org.name": "Organisation",
  "personaldata.dataType": "Data Type",
  "legal.consentType": "Consent",
  "lifecycle.location": "Location",
  "lifecycle.storage": "Storage",
  "lifecycle.sharing": "Sharing",
  "legal.publicInterest": "Lawful Basis",
  review_status: "Review Status",
  completeness: "Completeness",
  department: "Department",
};

const ROPA_ACTIVITY_ACTION_LABELS = {
  created: "New entry synced",
  updated: "ROPA fields updated",
  department_changed: "Department mapping changed",
};

const ROPA_ACTIVITY_SOURCE_LABELS = {
  asset_tracker: "Asset Tracker",
  dataforesight_ropa_automation: "DataForesight",
  dataforesight: "DataForesight",
};

function formatLogSource(source) {
  const key = String(source || "")
    .trim()
    .toLowerCase();
  if (!key) return "System";
  if (ROPA_ACTIVITY_SOURCE_LABELS[key]) return ROPA_ACTIVITY_SOURCE_LABELS[key];
  if (key.includes("dataforesight")) return "DataForesight";
  if (key.includes("asset")) return "Asset Tracker";
  return source
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTriggeredBy(value) {
  const raw = String(value || "").trim();
  if (!raw) return "System";
  if (raw.toLowerCase() === "dataforesight") return "DataForesight";
  if (!raw.includes("@")) return raw;
  const local = raw.split("@")[0] || raw;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function splitListValues(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function computeListDiff(oldVal, newVal) {
  const oldItems = splitListValues(oldVal);
  const newItems = splitListValues(newVal);
  const oldSet = new Set(oldItems.map((v) => v.toLowerCase()));
  const newSet = new Set(newItems.map((v) => v.toLowerCase()));
  const removed = oldItems.filter((v) => !newSet.has(v.toLowerCase()));
  const added = newItems.filter((v) => !oldSet.has(v.toLowerCase()));
  const kept = newItems.filter((v) => oldSet.has(v.toLowerCase()));
  return { removed, added, kept };
}

function ropaActivityFieldIcon(path) {
  if (path === "department" || path === "activity.dept") {
    return Building2;
  }
  if (path === "activity.dataOwner") return User;
  if (path.includes("dataType") || path.includes("personaldata")) {
    return FileText;
  }
  return FileText;
}

function ropaActivitySourceIcon(source) {
  const key = String(source || "").toLowerCase();
  if (key.includes("asset")) return Link2;
  if (key.includes("dataforesight")) return CloudUpload;
  return History;
}

function isListLikeField(path, oldVal, newVal) {
  if (path === "department" || path === "personaldata.dataType") return true;
  return (
    splitListValues(oldVal).length > 1 || splitListValues(newVal).length > 1
  );
}

const ROPA_ACTIVITY_LONG_LIST_THRESHOLD = 5;

function RopaListDiffChips({ listDiff, unchangedMessage }) {
  if (!listDiff) return null;
  const { removed, added, kept } = listDiff;
  if (removed.length === 0 && added.length === 0) {
    if (kept.length > 0) {
      return (
        <p className="text-[11px] text-slate-500">{unchangedMessage}</p>
      );
    }
    return null;
  }
  return (
    <div className="space-y-2">
      {removed.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase text-rose-600">
            Removed:
          </span>
          {removed.map((item) => (
            <span
              key={`rm-${item}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 line-through decoration-rose-400"
            >
              {item}
            </span>
          ))}
        </div>
      )}
      {added.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase text-emerald-700">
            Added:
          </span>
          {added.map((item) => (
            <span
              key={`add-${item}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200"
            >
              {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RopaActivityChangeDetail({ path, change }) {
  const label =
    ROPA_ACTIVITY_FIELD_LABELS[path] || path.replace(/\./g, " · ");
  const FieldIcon = ropaActivityFieldIcon(path);
  const oldVal = change?.old ?? "";
  const newVal = change?.new ?? "";
  const displayOld = oldVal || "—";
  const displayNew = newVal || "—";
  const oldItems = splitListValues(oldVal);
  const newItems = splitListValues(newVal);
  const isListLike = isListLikeField(path, oldVal, newVal);
  const listDiff =
    oldVal && newVal && isListLike ? computeListDiff(oldVal, newVal) : null;
  const hasListChange =
    listDiff &&
    (listDiff.added.length > 0 || listDiff.removed.length > 0);
  const isLongList =
    Math.max(oldItems.length, newItems.length) > ROPA_ACTIVITY_LONG_LIST_THRESHOLD;
  const useCompactList = isListLike && isLongList && hasListChange;
  const showListDiffOnly =
    isListLike && hasListChange && (useCompactList || isLongList);

  const beforeContent = useCompactList
    ? `${oldItems.length} item${oldItems.length === 1 ? "" : "s"}`
    : displayOld;
  const afterContent = useCompactList
    ? `${newItems.length} item${newItems.length === 1 ? "" : "s"}`
    : displayNew;

  const unchangedListMessage =
    path === "department"
      ? "Department mapping order updated; departments unchanged."
      : "List order updated; items unchanged.";

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600">
          <FieldIcon size={14} />
        </span>
        <span className="text-xs font-bold text-slate-800">{label}</span>
      </div>

      {showListDiffOnly ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Before
              </div>
              <div className="text-lg font-bold text-slate-700 tabular-nums mt-0.5">
                {oldItems.length}
              </div>
              <div className="text-[10px] text-slate-500">items</div>
            </div>
            <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-3 py-2 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">
                After
              </div>
              <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">
                {newItems.length}
              </div>
              <div className="text-[10px] text-slate-500">items</div>
            </div>
          </div>
          <RopaListDiffChips
            listDiff={listDiff}
            unchangedMessage={unchangedListMessage}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">
                Before
              </div>
              <div className="text-xs text-slate-600 leading-relaxed break-words">
                {beforeContent}
              </div>
            </div>
            <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-3 py-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 mb-1">
                After
              </div>
              <div className="text-xs font-semibold text-slate-900 leading-relaxed break-words">
                {afterContent}
              </div>
            </div>
          </div>

          {isListLike && listDiff && (
            <div className="mt-3">
              <RopaListDiffChips
                listDiff={listDiff}
                unchangedMessage={unchangedListMessage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RopaActivityLogItem({ log }) {
  const changes = log.changes || {};
  const changeEntries = Object.entries(changes);

  const actionLabel =
    ROPA_ACTIVITY_ACTION_LABELS[log.action] || log.action || "Update";

  const actionClass =
    log.action === "created"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : log.action === "department_changed"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-indigo-50 text-indigo-700 border-indigo-200";

  const sourceLabel = formatLogSource(log.source);
  const actorLabel = formatTriggeredBy(log.triggered_by);
  const SourceIcon = ropaActivitySourceIcon(log.source);

  return (
    <div className="border border-slate-200/80 rounded-xl bg-white shadow-sm overflow-hidden">
      <div className="px-4 pt-4 pb-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 tabular-nums">
              {formatDateTime(log.created_at)}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border ${actionClass}`}
            >
              {actionLabel}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <SourceIcon size={12} className="text-indigo-500 shrink-0" />
              via {sourceLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <User size={12} className="text-slate-400 shrink-0" />
              By {actorLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50/40">
        {changeEntries.length > 0 ? (
          <div className="space-y-3 pt-3">
            {changeEntries.map(([path, change]) => (
              <RopaActivityChangeDetail
                key={path}
                path={path}
                change={change}
              />
            ))}
          </div>
        ) : log.summary ? (
          <p className="text-xs text-slate-700 font-medium pt-3">
            {log.summary}
          </p>
        ) : (
          <p className="text-xs text-slate-400 italic pt-3">
            No field details recorded.
          </p>
        )}
      </div>
    </div>
  );
}

function RopaActivityLogDrawer({
  open,
  row,
  onClose,
  activityLogsPath,
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 10;

  const fetchLogs = useCallback(
    async (targetPage = 1) => {
      if (!row?.id || !activityLogsPath) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(targetPage),
          page_size: String(PAGE_SIZE),
        });
        const res = await apiFetch(
          `${activityLogsPath}/${row.id}/activity-logs?${params}`,
        );
        if (!res.ok) throw new Error("Failed to load activity logs");
        const data = await res.json();
        setLogs(data.items || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
        setPage(data.page || 1);
      } catch {
        showError("Failed to load change history");
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [row?.id, activityLogsPath],
  );

  useEffect(() => {
    if (open && row?.id) {
      setPage(1);
      fetchLogs(1);
    }
  }, [open, row?.id, fetchLogs]);

  if (!open || !row) return null;

  const title =
    row.database_label || row.display_name || row.entry_name || "Automated ROPA";

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-y-0 right-0 z-50 w-full md:w-1/2 max-w-[50vw] bg-white shadow-2xl border-l border-slate-200/80 flex flex-col h-screen overflow-hidden text-slate-900">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
              <History size={18} className="text-indigo-500 shrink-0" />
              Change History
            </h3>
            <p className="text-xs text-slate-600 font-semibold mt-1 truncate">
              {title}
            </p>
            {!loading && total > 0 && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                {total} change{total === 1 ? "" : "s"} recorded
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 transition-all focus:outline-none"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
              <Loader2 size={28} className="animate-spin text-indigo-500" />
              <span className="text-xs font-semibold">Loading history…</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <History size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-semibold">No changes recorded yet</p>
              <p className="text-xs mt-1 max-w-xs mx-auto">
                Updates from DataForesight pushes and Asset Tracker department
                mapping will appear here.
              </p>
            </div>
          ) : (
            logs.map((log) => (
              <RopaActivityLogItem key={log.id} log={log} />
            ))
          )}
        </div>

        {!loading && logs.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end flex-shrink-0">
            <Pagination
              page={page}
              pages={pages}
              total={total}
              pageSize={PAGE_SIZE}
              onPageChange={(p) => {
                setPage(p);
                fetchLogs(p);
              }}
              itemLabel="logs"
            />
          </div>
        )}
      </div>
    </>
  );
}

function flattenValue(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function rowsFromObject(data, labelMap = {}, { includeEmpty = false } = {}) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];
  const keys = includeEmpty
    ? [...new Set([...Object.keys(labelMap), ...Object.keys(data)])]
    : Object.keys(data);
  return keys
    .filter((k) => {
      if (includeEmpty) return true;
      const v = data[k];
      return v != null && v !== "" && !(Array.isArray(v) && !v.length);
    })
    .map((k) => ({
      label:
        labelMap[k] ||
        k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()),
      value: flattenValue(data[k]),
    }));
}

function isAnsweredValue(v) {
  if (v === false || v === 0) return true;
  if (Array.isArray(v)) return v.length > 0;
  return v != null && String(v).trim() !== "";
}

function getSubmissionAnswer(answers, qid) {
  if (!answers || qid == null) return undefined;
  const key = String(qid);
  if (answers[key] !== undefined) return answers[key];
  if (answers[qid] !== undefined) return answers[qid];
  return undefined;
}

function buildQuestionMap(questions) {
  const map = new Map();
  (questions || []).forEach((q) => {
    map.set(String(q.id), q);
  });
  return map;
}

function sortAnswerKeys(a, b) {
  const na = parseInt(String(a).replace(/\D/g, ""), 10);
  const nb = parseInt(String(b).replace(/\D/g, ""), 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b));
}

/** Map answers to current question ids (handles g1/uuid id drift after questionnaire changes). */
function buildAnswerLookup(questions, answers) {
  const sortedQs = [...(questions || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const lookup = new Map();

  sortedQs.forEach((q) => {
    const v = getSubmissionAnswer(answers, q.id);
    if (isAnsweredValue(v)) lookup.set(String(q.id), v);
  });

  const orphanEntries = Object.entries(answers || {})
    .filter(([, v]) => isAnsweredValue(v))
    .filter(([k]) => !sortedQs.some((q) => String(q.id) === String(k)))
    .sort(([a], [b]) => sortAnswerKeys(a, b));

  if (!orphanEntries.length) {
    return (qid) => lookup.get(String(qid));
  }

  if (lookup.size === 0 && orphanEntries.length === sortedQs.length) {
    sortedQs.forEach((q, i) => lookup.set(String(q.id), orphanEntries[i][1]));
    return (qid) => lookup.get(String(qid));
  }

  const byCat = {};
  sortedQs.forEach((q) => {
    const cat = q.category || "general";
    if (!byCat[cat]) byCat[cat] = [];
    byCat[cat].push(q);
  });

  let orphanIndex = 0;
  sortTprmCategories(Object.keys(byCat)).forEach((cat) => {
    const ordered = [...byCat[cat]].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    ordered
      .filter((q) => !lookup.has(String(q.id)))
      .forEach((q) => {
        if (orphanIndex < orphanEntries.length) {
          lookup.set(String(q.id), orphanEntries[orphanIndex][1]);
          orphanIndex += 1;
        }
      });
  });

  return (qid) => {
    const key = String(qid);
    if (lookup.has(key)) return lookup.get(key);
    return getSubmissionAnswer(answers, qid);
  };
}

const TPRM_CATEGORY_META = {
  general: "General Information",
  security: "Cybersecurity & Data",
  compliance: "Compliance & Regulatory",
  financial: "Financial Stability",
  operational: "Operational Risk",
  legal: "Legal",
  reputation: "Reputation",
  other: "Other Responses",
};

function tprmSectionTitle(cat) {
  return (
    TPRM_CATEGORY_META[cat] ||
    (cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : "General")
  );
}

function tprmVendorName(submission, questions = []) {
  const answers = submission.answers || {};
  const snapshot = submission.questions_snapshot || [];
  const displayQuestions = snapshot.length ? snapshot : questions;
  const getAnswer = buildAnswerLookup(displayQuestions, answers);
  const nameQ = displayQuestions.find((q) =>
    /legal name|organisation|organization|vendor name|company name/i.test(
      q.text || "",
    ),
  );
  if (nameQ) {
    const val = getAnswer(nameQ.id);
    if (isAnsweredValue(val)) return String(val);
  }
  const firstGeneral = displayQuestions.find(
    (q) => (q.category || "general") === "general",
  );
  if (firstGeneral) {
    const val = getAnswer(firstGeneral.id);
    if (isAnsweredValue(val)) return String(val);
  }
  return "Third-Party Risk Assessment";
}

const PIA_SECTION_LABELS = {
  general: "General Info",
  dataDetails: "Data Details",
  dataFlow: "Data Flow",
  risk: "Risk Assessment",
  vendorControls: "Vendor & Controls",
  safeguards: "Safeguards & Review",
};

const PIA_FIELD_LABELS = {
  vendor: "Vendor",
  projectName: "Project Name",
  ownerDept: "Owner Department",
  procDesc: "Processing Description",
  bizPurpose: "Business Purpose",
  legalBasis: "Legal Basis",
  duration: "Processing Duration",
  dataTypes: "Data Types",
  sensitiveData: "Sensitive Data",
  dataSubjects: "Data Subjects",
  volume: "Data Volume",
  retention: "Retention Period",
  appOrigin: "Application Origin",
  appDest: "Application Destination",
  storageLocations: "Storage Locations",
  otherStorage: "Other Storage",
  collectionMethod: "Collection Method",
  internalTransfers: "Internal Transfers",
  externalTransfers: "External Transfers",
  crossBorder: "Cross-Border Transfers",
  processors: "Processors",
  likelihood: "Likelihood",
  impact: "Impact",
  riskCategory: "Risk Category",
  mitigation: "Mitigation Measures",
  controlTypes: "Control Types",
  complianceStatus: "Compliance Status",
  subProcessors: "Sub-Processors",
  dataLocations: "Data Locations",
  certifications: "Certifications",
  vendorContact: "Vendor Contact",
  contractExpiry: "Contract Expiry",
  gdprSafeguards: "GDPR Safeguards",
  dpdpSafeguards: "DPDP Safeguards",
  transferJustification: "Transfer Justification",
  reviewerName: "Reviewer Name",
  reviewDate: "Review Date",
  acknowledged: "Acknowledged",
};

const ROPA_SECTION_LABELS = {
  org: "Organisation Details",
  activity: "Processing Activity",
  legal: "Legal Basis",
  principals: "Data Principals",
  personaldata: "Personal Data",
  lifecycle: "Processing Lifecycle",
  sharing: "Data Sharing",
  retention: "Retention & Erasure",
  security: "Security Safeguards",
  rights: "Rights Handling",
  incident: "Incident Management",
  approval: "Approval & Sign-off",
};

const ROPA_FIELD_LABELS = {
  name: "Name",
  role: "Role",
  address: "Address",
  email: "Email",
  phone: "Phone",
  grievance: "Grievance Officer",
  sdf: "Significant Data Fiduciary",
  lastUpdated: "Last Updated",
  dept: "Department",
  purpose: "Purpose",
  desc: "Description",
  consent: "Consent",
  employment: "Employment",
  legalObligation: "Legal Obligation",
  publicInterest: "Public Interest",
  customers: "Customers",
  employees: "Employees",
  applicants: "Applicants",
  vendors: "Vendors",
  minors: "Minors",
  identity: "Identity Data",
  contact: "Contact Data",
  financial: "Financial Data",
  auth: "Authentication Data",
  usageDevice: "Usage / Device Data",
  collection: "Collection",
  storage: "Storage",
  processing: "Processing",
  access: "Access",
  encryption: "Encryption",
  accessCtrl: "Access Controls",
  logging: "Logging & Monitoring",
  incident: "Incident Response",
  backup: "Backup & DR",
  identification: "Breach Identification",
  dpbNotification: "DPB Notification",
  timeline: "Notification Timeline",
  communication: "User Communication",
  dataOwner: "Data Owner",
  consentType: "Consent Type",
  requestedTypes: "Requested Types",
  dataType: "Data Type",
  location: "Location",
};

const ROPA_ROW_COLUMNS = {
  sharing: [
    { key: "recipient", label: "Recipient" },
    { key: "role", label: "Role / Type" },
    { key: "purpose", label: "Purpose" },
    { key: "location", label: "Location" },
  ],
  retention: [
    { key: "category", label: "Data Category" },
    { key: "period", label: "Retention Period" },
    { key: "trigger", label: "Deletion Trigger" },
    { key: "method", label: "Deletion Method" },
  ],
  rights: [
    { key: "right", label: "Right" },
    { key: "process", label: "Process" },
    { key: "sla", label: "SLA (days)" },
  ],
  approval: [
    { key: "role", label: "Role" },
    { key: "name", label: "Name" },
    { key: "signature", label: "Signature Ref." },
    { key: "date", label: "Date" },
  ],
};

function OverviewCard({ title, children }) {
  return (
    <div className="rounded-[14px] p-4 bg-white border border-indigo-500/10 mb-3">
      {title && (
        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">
          {title}
        </h4>
      )}
      {children}
    </div>
  );
}

function OverviewRow({ label, value }) {
  const text = flattenValue(value);
  return (
    <div className="flex gap-3 mb-2 text-xs items-start">
      <span className="min-w-[130px] font-semibold text-slate-600 shrink-0">
        {label}
      </span>
      <span className="text-slate-900 flex-1 leading-relaxed">{text}</span>
    </div>
  );
}

function SectionScoresPanel({ scores, titleFn = (k) => k }) {
  const entries = Object.entries(scores || {});
  if (!entries.length) {
    return (
      <p className="text-xs text-slate-500 m-0">No section scores available.</p>
    );
  }
  return (
    <div className="flex flex-col gap-2.5">
      {entries.map(([sec, score]) => (
        <div key={sec} className="flex items-center gap-2.5 text-xs">
          <span className="flex-1 font-semibold text-slate-900">
            {titleFn(sec)}
          </span>
          <div className="w-[100px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${score}%`, background: scoreColor(score) }}
            />
          </div>
          <span
            className="font-bold w-10 text-right"
            style={{ color: scoreColor(score) }}
          >
            {score}%
          </span>
        </div>
      ))}
    </div>
  );
}

function RowsDetailTable({ columns, rows }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto mt-1">
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="text-left px-2.5 py-2 bg-slate-50 text-slate-600 font-bold border-b border-indigo-500/10"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className="px-2.5 py-2 text-slate-900 border-b border-slate-100 align-top"
                >
                  {flattenValue(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function sortTprmCategories(categories) {
  const catOrder = Object.keys(TPRM_CATEGORY_META).filter((c) => c !== "other");
  return [...categories].sort((a, b) => {
    const ai = catOrder.indexOf(a);
    const bi = catOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function TprmSectionBlock({
  category,
  questions,
  answers,
  sectionScore,
  getAnswer,
  attachmentsByQuestion = {},
}) {
  return (
    <div className="mb-3.5 rounded-xl border border-indigo-500/10 overflow-hidden">
      <div className="px-3.5 py-2.5 bg-slate-50 flex items-center justify-between gap-3">
        <span className="text-xs font-bold text-slate-900">
          {tprmSectionTitle(category)}
        </span>
        {sectionScore != null && (
          <span
            className="text-[11px] font-bold px-2.5 py-0.5 rounded-full shrink-0"
            style={{
              color: scoreColor(sectionScore),
              background: scoreBg(sectionScore),
              border: `1px solid ${scoreColor(sectionScore)}30`,
            }}
          >
            {sectionScore}%
          </span>
        )}
      </div>
      <div className="px-3.5 py-3">
        {questions.map((q, idx) => {
          const ans = getAnswer(q.id);
          const answered = isAnsweredValue(ans);
          const isLast = idx >= questions.length - 1;
          return (
            <div
              key={q.id}
              className={`${isLast ? "" : "mb-3 pb-3 border-b border-slate-100"}`}
            >
              <div className="flex gap-2 items-start mb-1.5">
                <span className="text-[10px] font-bold text-indigo-600 bg-indigo-500/10 rounded-md px-1.5 py-0.5 shrink-0 mt-px">
                  Q{idx + 1}
                </span>
                <div className="text-xs font-semibold text-slate-700 leading-relaxed flex-1">
                  {q.text}
                  {q.required && <span className="text-red-500 ml-1">*</span>}
                </div>
              </div>
              <div
                className={`ml-9 text-xs leading-relaxed py-2 px-3 rounded-lg ${
                  answered
                    ? "text-slate-900 font-medium bg-slate-50 border border-indigo-500/10"
                    : "text-slate-400 font-normal bg-neutral-50 border border-slate-100"
                }`}
              >
                {flattenValue(ans)}
              </div>
              {(attachmentsByQuestion[q.id] || []).length > 0 && (
                <div className="ml-9 mt-2 space-y-2">
                  {(attachmentsByQuestion[q.id] || []).map((file) => (
                    <AttachmentPreviewRow
                      key={file.id}
                      file={file}
                      previewPath={tprmAttachmentPreviewPath(file)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuestionnaireDetails({
  submission,
  questions,
  loading,
  attachmentsByQuestion = {},
}) {
  const answers = submission?.answers || {};
  const sectionScores = submission?.section_scores || {};
  const qMap = buildQuestionMap(questions);
  const snapshot = submission?.questions_snapshot || [];
  const displayQuestions = snapshot.length ? snapshot : questions;
  const getAnswer = buildAnswerLookup(displayQuestions, answers);

  if (loading && !displayQuestions.length) {
    return <p className="text-xs text-slate-500 m-0">Loading questionnaire…</p>;
  }

  if (displayQuestions.length > 0) {
    const grouped = {};
    [...displayQuestions]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((q) => {
        const cat = q.category || "general";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(q);
      });

    const sortedCats = sortTprmCategories(Object.keys(grouped));

    return (
      <div>
        {sortedCats.map((cat) => (
          <TprmSectionBlock
            key={cat}
            category={cat}
            questions={grouped[cat]}
            answers={answers}
            sectionScore={sectionScores[cat]}
            getAnswer={getAnswer}
            attachmentsByQuestion={attachmentsByQuestion}
          />
        ))}
      </div>
    );
  }

  const answeredEntries = Object.entries(answers).filter(([, v]) =>
    isAnsweredValue(v),
  );

  if (!answeredEntries.length) {
    return (
      <p className="text-xs text-slate-500 m-0">
        No response data recorded for this submission.
      </p>
    );
  }

  const grouped = {};
  answeredEntries.forEach(([qid, ans]) => {
    const q = qMap.get(String(qid));
    const cat = q?.category || "other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ qid, q, ans });
  });

  const sortedCats = sortTprmCategories(Object.keys(grouped));

  return (
    <div>
      {sortedCats.map((cat) => (
        <div
          key={cat}
          className="mb-3.5 rounded-xl border border-indigo-500/10 overflow-hidden"
        >
          <div className="px-3.5 py-2.5 bg-slate-50 text-xs font-bold text-slate-900">
            {tprmSectionTitle(cat)}
          </div>
          <div className="px-3.5 py-3">
            {grouped[cat].map(({ qid, q, ans }) => (
              <div
                key={qid}
                className="mb-2.5 pb-2.5 border-b border-slate-100 last:mb-0 last:pb-0 last:border-0"
              >
                <div className="text-xs font-semibold text-slate-700 mb-1 leading-normal">
                  {q?.text || `Question ${qid}`}
                </div>
                <div className="text-xs text-slate-900 py-2 px-3 rounded-lg bg-slate-50 border border-indigo-500/10">
                  {flattenValue(ans)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FormSubmissionsPage({ config, embedded = false }) {
  const navigate = useNavigate();
  const { departments } = useDepartments();
  const [submissions, setSubmissions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const PAGE_SIZE = 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [backendStats, setBackendStats] = useState(null);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(null);
  const [logRow, setLogRow] = useState(null);

  const Icon = config.icon || FileText;

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        sort: sortKey,
        sort_dir: sortDir,
      });
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (deptFilter) params.set("department_id", deptFilter);
      if (statusFilter) params.set("status", statusFilter);
      const res = await apiFetch(`${config.apiPath}?${params}`);
      if (!res.ok) throw new Error("Failed to load submissions");
      const data = await res.json();
      setSubmissions(data.submissions || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      if (data.stats) setBackendStats(data.stats);
      if (data.questions?.length) setQuestions(data.questions);
    } catch (e) {
      setError(e.message || "Could not load submissions");
    } finally {
      setLoading(false);
    }
  }, [
    config.apiPath,
    page,
    debouncedSearch,
    deptFilter,
    statusFilter,
    sortKey,
    sortDir,
  ]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [deptFilter, statusFilter, sortKey, sortDir]);

  useEffect(() => {
    if (!config.questionnairePath) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(config.questionnairePath);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setQuestions(data.items || []);
        }
      } catch {
        if (!cancelled) setQuestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config.questionnairePath]);

  const enrichedSubmissions = useMemo(() => {
    if (!config.enrichSubmission) return submissions;
    return submissions.map((s) => config.enrichSubmission(s, { questions }));
  }, [submissions, questions, config]);

  const deptOptions = useMemo(() => {
    if (!departments || departments.length <= 1) return [];
    return departments
      .filter((d) => d.id)
      .map((d) => [d.id, d.name || d.code || "Department"]);
  }, [departments]);

  const stats = useMemo(
    () =>
      config.computeStats(enrichedSubmissions, { total, stats: backendStats }),
    [enrichedSubmissions, config, total, backendStats],
  );

  const handleCardClick = (tag) => {
    if (tag === "AVG") return;
    if (tag === "TOTAL") {
      setStatusFilter("");
    } else {
      setStatusFilter((prev) => (prev === tag ? "" : tag));
    }
  };

  const sorted = enrichedSubmissions;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k)
      return <span className="text-slate-300 text-[10px]">↕</span>;
    return sortDir === "asc" ? (
      <ChevronUp size={12} className="text-indigo-500" />
    ) : (
      <ChevronDown size={12} className="text-indigo-500" />
    );
  };

  return (
    <div
      className={
        embedded
          ? "space-y-3"
          : "bg-slate-100 font-sans min-h-[calc(100vh-3rem)]"
      }
    >
      {!embedded && (
        <header className="bg-white border-b border-slate-300 px-4 sm:px-6 py-3 flex items-center gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0 bg-gradient-to-br from-blue-500 to-indigo-500">
              <Icon size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
                {config.title}
              </h1>
              <p className="text-xs text-slate-700 hidden sm:block">
                {config.subtitle}
              </p>
            </div>
          </div>

          <div className="hidden md:flex flex-1 justify-center max-w-sm px-2">
            <div className="flex items-center gap-2 w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg">
              <Search size={14} className="text-slate-400 flex-shrink-0" />
              <input
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                name="form-submission-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by submitted by…"
                className="w-full border-0 outline-none text-xs text-slate-900 bg-transparent"
              />
            </div>
          </div>

          {deptOptions.length > 0 && (
            <div className="hidden lg:block flex-shrink-0">
              <select
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
                className="text-xs text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5 bg-white min-w-[140px]"
              >
                <option value="">All departments</option>
                {deptOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={() => navigate(config.backPath)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap shadow-md shadow-blue-500/35 bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-600 hover:shadow-lg hover:brightness-105 active:scale-[0.98]"
            >
              <ArrowLeft size={12} />
              <span className="hidden sm:inline">{config.backLabel}</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>
        </header>
      )}

      <div className={embedded ? "space-y-3" : "p-3 sm:p-4 space-y-3"}>
        {embedded && (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="flex items-center gap-2 flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg">
              <Search size={14} className="text-slate-400 flex-shrink-0" />
              <input
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                name="form-submission-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by submitted by…"
                className="w-full border-0 outline-none text-xs text-slate-900 bg-transparent"
              />
            </div>
            {deptOptions.length > 0 && (
              <select
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
                className="text-xs text-slate-900 border border-slate-200 rounded-lg px-2.5 py-2 bg-white min-w-[160px]"
              >
                <option value="">All departments</option>
                {deptOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        {!embedded && (
          <div className="md:hidden">
            <div className="flex items-center gap-2 w-full px-3 py-2 bg-white border border-slate-300 rounded-lg">
              <Search size={14} className="text-slate-400 flex-shrink-0" />
              <input
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                name="form-submission-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by submitted by…"
                className="w-full border-0 outline-none text-xs text-slate-900 bg-transparent"
              />
            </div>
            {deptOptions.length > 0 && (
              <select
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
                className="mt-2 w-full text-xs text-slate-900 border border-slate-300 rounded-lg px-2.5 py-2 bg-white"
              >
                <option value="">All departments</option>
                {deptOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((card) => (
            <DashboardStatCard
              key={card.title}
              {...card}
              loading={loading}
              onClick={
                card.tag !== "AVG" ? () => handleCardClick(card.tag) : undefined
              }
              active={
                card.tag === "TOTAL" ? !statusFilter : statusFilter === card.tag
              }
            />
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-600">
              Loading submissions…
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <AlertTriangle size={32} className="mx-auto mb-3 text-red-500" />
              <p className="text-sm text-red-700 font-semibold">{error}</p>
            </div>
          ) : sorted.length === 0 ? (
            <div className="py-16 text-center">
              <ClipboardList
                size={40}
                className="mx-auto mb-3 text-slate-300"
              />
              <p className="text-sm font-semibold text-slate-600">
                {search ? "No matching submissions" : "No submissions yet"}
              </p>
            </div>
          ) : (
            <>
              <div className="w-full max-w-full overflow-auto scrollbar-thin max-h-[calc(3.5rem+5*3.75rem)]">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr className="bg-slate-50 border-b-2 border-indigo-500/10">
                      <th className="px-3 py-3.5 text-left font-bold text-[11px] text-slate-600 uppercase tracking-wider whitespace-nowrap w-12">
                        S.No.
                      </th>
                      {config.columns.map((col) => (
                        <th
                          key={col.label}
                          onClick={() => col.key && toggleSort(col.key)}
                          className={`px-5 py-3.5 text-left text-[11px] font-bold text-slate-600 uppercase tracking-wide whitespace-nowrap bg-slate-50 ${
                            col.key ? "cursor-pointer select-none" : ""
                          }`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {col.key && <SortIcon k={col.key} />}
                          </span>
                        </th>
                      ))}
                      <th className="px-5 py-3.5 text-left font-bold text-[11px] text-slate-600 uppercase tracking-wider whitespace-nowrap">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row, i) => (
                      <tr
                        key={row.id || i}
                        className={`border-b border-indigo-500/10 transition-colors hover:bg-indigo-50/50 ${
                          i % 2 === 0 ? "bg-white" : "bg-indigo-50/20"
                        }`}
                      >
                        <td className="px-3 py-3.5 text-xs font-semibold text-slate-500 tabular-nums">
                          {(page - 1) * PAGE_SIZE + i + 1}
                        </td>
                        {config.columns.map((col) => (
                          <td key={col.label} className="px-5 py-3.5">
                            {col.render
                              ? col.render(row)
                              : (row[col.key] ?? "—")}
                          </td>
                        ))}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelected(row)}
                              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-[10px] bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-[11px] font-semibold border-0 cursor-pointer shadow-md shadow-indigo-500/30"
                            >
                              <Eye size={12} /> View
                            </button>
                            {config.showActivityLog && config.activityLogsPath && (
                              <button
                                type="button"
                                onClick={() => setLogRow(row)}
                                title="View change history"
                                className="inline-flex items-center justify-center w-8 h-8 rounded-[10px] border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-200 transition-colors shadow-sm"
                              >
                                <History size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > 0 && (
                <div className="px-4 py-3 border-t border-slate-100">
                  <Pagination
                    page={page}
                    pages={pages}
                    total={total}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                    itemLabel="submissions"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selected && (
        <DetailModal
          submission={selected}
          onClose={() => setSelected(null)}
          config={config}
          questions={questions}
        />
      )}

      {config.showActivityLog && (
        <RopaActivityLogDrawer
          open={Boolean(logRow)}
          row={logRow}
          onClose={() => setLogRow(null)}
          activityLogsPath={config.activityLogsPath}
        />
      )}
    </div>
  );
}

function DetailModal({
  submission,
  onClose,
  config,
  questions: questionsProp = [],
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [modalQuestions, setModalQuestions] = useState(questionsProp);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [attachmentsByQuestion, setAttachmentsByQuestion] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "details", label: config.responsesTabLabel || "Full Details" },
  ];
  const questions = modalQuestions.length ? modalQuestions : questionsProp;
  const ctx = { questions, questionsLoading, attachmentsByQuestion };

  useEffect(() => {
    setActiveTab("overview");
    setAttachmentsByQuestion({});
  }, [submission]);

  useEffect(() => {
    if (
      !config.attachmentsListPath ||
      !submission?.department_id ||
      !submission?.user_id
    ) {
      setAttachmentsByQuestion({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          department_id: submission.department_id,
          user_id: submission.user_id,
        });
        const res = await apiFetch(`${config.attachmentsListPath}?${params}`);
        if (!res.ok) throw new Error("Failed to load attachments");
        const rows = await res.json();
        if (cancelled) return;
        const grouped = {};
        (rows || []).forEach((file) => {
          if (!file?.question_id) return;
          if (!grouped[file.question_id]) grouped[file.question_id] = [];
          grouped[file.question_id].push(file);
        });
        setAttachmentsByQuestion(grouped);
      } catch {
        if (!cancelled) setAttachmentsByQuestion({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    config.attachmentsListPath,
    submission?.id,
    submission?.department_id,
    submission?.user_id,
  ]);

  useEffect(() => {
    if (questionsProp.length) setModalQuestions(questionsProp);
  }, [questionsProp]);

  useEffect(() => {
    if (!config.questionnairePath || !submission) return undefined;
    let cancelled = false;
    setQuestionsLoading(true);
    (async () => {
      try {
        const res = await apiFetch(config.questionnairePath);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setModalQuestions(data.items || data.questions || []);
        }
      } catch {
        if (!cancelled && !questionsProp.length) setModalQuestions([]);
      } finally {
        if (!cancelled) setQuestionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config.questionnairePath, submission?.id, questionsProp.length]);

  const header = config.getHeaderMeta(submission, ctx);

  const getExportSections = (forExcel = false) => {
    const exportCtx = { ...ctx, forExcel };
    if (forExcel && config.buildExcelExportSections) {
      return config.buildExcelExportSections(submission, exportCtx);
    }
    if (config.buildExportSections) {
      return config.buildExportSections(submission, exportCtx);
    }
    return [
      {
        heading: "Submission",
        rows: [
          { label: "Title", value: header.title },
          { label: "Submitted", value: formatDate(submission.created_at) },
        ],
      },
    ];
  };

  const getExportPayload = (forExcel = false) => ({
    title: header.title,
    subtitle: `${config.title} · ${formatDate(submission.created_at)}`,
    module: config.pdfPrefix || "Report",
    organization: header.title,
    filename: `${config.pdfPrefix || "submission"}_${safeExportFilename(header.title)}`,
    sections: getExportSections(forExcel),
  });

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      await exportSubmissionPdf(getExportPayload(false));
    } catch (err) {
      showError(err?.message || "Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    if (
      config.questionnairePath &&
      questionsLoading &&
      !modalQuestions.length
    ) {
      showInfo(
        "Questionnaire is still loading. Please wait a moment and try again.",
      );
      return;
    }
    setExportingExcel(true);
    try {
      await exportSubmissionExcel(getExportPayload(true));
    } catch (err) {
      showError(err?.message || "Failed to export Excel");
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <Modal open onClose={onClose} bare zIndex={200} align="top">
      <div className="w-full max-w-[860px] bg-white rounded-[20px] border border-indigo-500/10 shadow-2xl shadow-slate-900/15 font-sans overflow-hidden">
        <div className="px-6 pt-5 pb-0 border-b border-indigo-500/10">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-500/15 flex items-center justify-center shrink-0">
              <Building2 size={20} className="text-indigo-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-extrabold text-slate-900 m-0 font-display">
                  {header.title}
                </h2>
                {header.gauge != null && (
                  <ScoreRing
                    value={header.gauge}
                    size={52}
                    stroke={4}
                    color={header.gaugeColor || "#6366f1"}
                    label={header.gaugeLabel ?? `${header.gauge}%`}
                  />
                )}
              </div>
              <div className="flex items-center gap-2.5 mt-2 flex-wrap">
                {header.chip && (
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      background: header.chip.bg,
                      color: header.chip.color,
                      border: `1px solid ${header.chip.color}40`,
                    }}
                  >
                    {header.chip.label}
                  </span>
                )}
                <span className="text-xs text-slate-500">
                  {formatDate(submission.created_at)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={exportingPdf || exportingExcel}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-indigo-500/20 text-xs font-semibold ${
                  exportingPdf
                    ? "bg-slate-100 text-indigo-800 cursor-wait opacity-70"
                    : "bg-indigo-50 text-indigo-800 hover:bg-indigo-100 cursor-pointer"
                }`}
              >
                <FileDown size={14} />
                {exportingPdf ? "Generating…" : "Export PDF"}
              </button>
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={exportingExcel || exportingPdf}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-emerald-500/25 text-xs font-semibold ${
                  exportingExcel
                    ? "bg-slate-100 text-emerald-800 cursor-wait opacity-70"
                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 cursor-pointer"
                }`}
              >
                <FileSpreadsheet size={14} />
                {exportingExcel ? "Generating…" : "Excel"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-[34px] h-[34px] rounded-[10px] border border-indigo-500/15 bg-white text-slate-600 flex items-center justify-center cursor-pointer hover:bg-slate-50"
              >
                <XIcon size={16} />
              </button>
            </div>
          </div>

          {activeTab === "overview" && header.statCards?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {header.statCards.map((card) => (
                <DashboardStatCard key={card.title} {...card} allowWrap />
              ))}
            </div>
          )}

          <div className="flex gap-0 border-t border-indigo-500/10">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2.5 border-0 border-b-2 bg-transparent cursor-pointer text-xs ${
                  activeTab === t.id
                    ? "border-indigo-500 font-bold text-indigo-600"
                    : "border-transparent font-medium text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 pt-5 pb-6 max-h-[58vh] overflow-y-auto">
          {activeTab === "overview" && (
            <div>
              {config.renderOverview ? (
                config.renderOverview(submission, ctx)
              ) : (
                <p className="text-xs text-slate-500">
                  Submitted on {formatDate(submission.created_at)}
                </p>
              )}
            </div>
          )}
          {activeTab === "details" && config.renderDetails(submission, ctx)}
        </div>
      </div>
    </Modal>
  );
}

function DataSection({ title, data, labelMap = {} }) {
  if (!data || typeof data !== "object") return null;
  const entries = Object.entries(data).filter(
    ([, v]) => v != null && v !== "" && !(Array.isArray(v) && !v.length),
  );
  if (!entries.length) return null;
  return (
    <div className="mb-3 rounded-xl border border-indigo-500/10 overflow-hidden">
      <div className="px-3.5 py-2.5 bg-slate-50 text-xs font-bold text-slate-900">
        {title}
      </div>
      <div className="px-3.5 py-3">
        {entries.map(([k, v]) => (
          <div key={k} className="flex gap-3 mb-2 text-xs">
            <span className="min-w-[140px] font-semibold text-slate-600">
              {labelMap[k] ||
                k
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (c) => c.toUpperCase())}
            </span>
            <span className="text-slate-900 flex-1 leading-normal">
              {flattenValue(v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusChip({ label, color, bg }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
      style={{ background: bg, color, border: `1px solid ${color}30` }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

/** Gap Assessment-style score ring + horizontal progress bar. */
function SubmissionScoreCell({ value }) {
  if (value == null || value === "" || Number.isNaN(Number(value))) {
    return <span className="text-slate-400 text-xs">—</span>;
  }
  const sc = Math.min(100, Math.max(0, Math.round(Number(value))));
  const c = scoreColor(sc);
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative shrink-0">
        <ArcGauge value={sc} size={40} stroke={4} color={c} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[9px] font-extrabold" style={{ color: c }}>
            {sc}%
          </span>
        </div>
      </div>
      <div className="w-[60px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${sc}%`, background: c }}
        />
      </div>
    </div>
  );
}

function ComplianceStatusCell({ score, label }) {
  if (score == null && !label) {
    return <span className="text-slate-400 text-xs">—</span>;
  }
  const sc =
    score != null
      ? Math.min(100, Math.max(0, Math.round(Number(score))))
      : null;
  const c = sc != null ? scoreColor(sc) : "#64748b";
  const bg = sc != null ? scoreBg(sc) : "#64748b18";
  const text = label || (sc != null ? scoreLabel(sc) : "—");
  return <StatusChip label={text} color={c} bg={bg} />;
}

function OrganisationColumn({ title, subtitle }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-[34px] h-[34px] rounded-[10px] shrink-0 bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-500/15 flex items-center justify-center">
        <Building2 size={15} className="text-indigo-500" />
      </div>
      <div>
        <div className="font-semibold text-slate-900">{title || "—"}</div>
        {subtitle ? (
          <div className="text-[11px] text-slate-400 mt-px">{subtitle}</div>
        ) : null}
      </div>
    </div>
  );
}

function AnsweredColumn({ answered, total }) {
  return (
    <>
      <span className="font-semibold text-slate-900">{answered ?? "—"}</span>
      {total != null ? (
        <span className="text-slate-400 text-[11px]"> / {total}</span>
      ) : null}
    </>
  );
}

function SubmittedColumn({ date }) {
  return (
    <div className="flex items-center gap-1.5">
      <Calendar size={12} className="text-slate-400" />
      <span className="text-slate-600 text-xs">{formatDate(date)}</span>
    </div>
  );
}

/** Gap Assessment table columns: Organisation through Submitted. */
function buildGapSubmissionColumns({
  orgTitle,
  orgSubtitle,
  scoreKey = "form_score",
  scoreValue,
  statusLabel,
  statusScore,
  answeredValue,
  totalValue,
}) {
  const resolveScore = (row) =>
    scoreValue ? scoreValue(row) : row[scoreKey];

  return [
    {
      key: "display_name",
      label: "Organisation",
      render: (row) => (
        <OrganisationColumn
          title={orgTitle(row)}
          subtitle={orgSubtitle?.(row)}
        />
      ),
    },
    {
      key: "department_name",
      label: "Department",
      render: (row) => (
        <span className="text-xs font-semibold text-slate-700">
          {row.department_name || "—"}
        </span>
      ),
    },
    {
      key: "submitted_by_name",
      label: "Submitted By",
      render: (row) => (
        <span className="text-xs text-slate-600">
          {row.submitted_by_name || "—"}
        </span>
      ),
    },
    {
      key: scoreKey,
      label: "Score",
      render: (row) => <SubmissionScoreCell value={resolveScore(row)} />,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const sc = resolveScore(row);
        const label = statusLabel?.(row);
        return (
          <ComplianceStatusCell
            score={statusScore ? statusScore(row) : sc}
            label={label}
          />
        );
      },
    },
    {
      key: "answered",
      label: "Answered",
      render: (row) => (
        <AnsweredColumn
          answered={answeredValue(row)}
          total={totalValue?.(row)}
        />
      ),
    },
    {
      key: "created_at",
      label: "Submitted",
      render: (row) => <SubmittedColumn date={row.created_at} />,
    },
  ];
}

const ROPA_GAP_COLUMNS = buildGapSubmissionColumns({
  orgTitle: (row) => row.org_name || row.display_name || "—",
  orgSubtitle: (row) =>
    row.activity_name ||
    (row.id ? `ID: ${String(row.id).slice(0, 8)}…` : null),
  scoreValue: (row) => row.form_score,
  statusLabel: (row) =>
    row.compliance_level ||
    (row.form_score != null ? scoreLabel(row.form_score) : null),
  answeredValue: (row) => row.sections_filled,
  totalValue: (row) => row.sections_total ?? 12,
});

const PIA_CONFIG = {
  apiPath: "/pia/submissions",
  backPath: "/user/pia",
  backLabel: "Back to PIA",
  title: "PIA Submissions",
  pdfPrefix: "PIA",
  subtitle: "All submitted Privacy Impact Assessments",
  icon: ShieldCheck,
  computeStats: (subs, meta = {}) => {
    const total = meta.stats?.total ?? meta.total ?? subs.length;
    const highRisk =
      meta.stats?.high_risk ??
      subs.filter((s) => ["High", "Critical"].includes(s.risk_level)).length;
    const sensitive =
      meta.stats?.sensitive ??
      subs.filter(
        (s) =>
          (s.form_data?.dataDetails?.sensitiveData || "").toLowerCase() ===
          "yes",
      ).length;
    const avgRisk =
      meta.stats?.avg_risk ??
      (total > 0
        ? (subs.reduce((a, s) => a + (s.risk_score || 0), 0) / total).toFixed(1)
        : "0");
    return [
      {
        tag: "TOTAL",
        title: "Submissions",
        value: total,
        subtitle: "Completed PIAs",
        accent: "#7dd3fc",
        icon: Users,
      },
      {
        tag: "RISK",
        title: "High / Critical",
        value: highRisk,
        subtitle: "Elevated risk level",
        accent: "#fca5a5",
        icon: ShieldAlert,
      },
      {
        tag: "SENS",
        title: "Sensitive Data",
        value: sensitive,
        subtitle: "Processes sensitive data",
        accent: "#fcd34d",
        icon: AlertTriangle,
      },
      {
        tag: "AVG",
        title: "Avg Risk Score",
        value: avgRisk,
        subtitle: "Likelihood × impact",
        accent: "#a5b4fc",
        icon: BarChart3,
      },
    ];
  },
  searchFilter: (r, q) =>
    (r.display_name || "").toLowerCase().includes(q) ||
    (r.vendor || "").toLowerCase().includes(q) ||
    (r.risk_level || "").toLowerCase().includes(q) ||
    (r.department_name || "").toLowerCase().includes(q),
  columns: buildGapSubmissionColumns({
    orgTitle: (row) => {
      const vendor = (row.vendor || row.org_name || "").trim();
      if (vendor) return vendor;
      return row.display_name || row.project_name || "—";
    },
    orgSubtitle: (row) => {
      const vendor = (row.vendor || row.org_name || "").trim();
      const project = (row.project_name || "").trim();
      if (vendor && project) return project;
      return null;
    },
    scoreValue: (row) => row.form_score,
    statusLabel: (row) =>
      row.risk_level ||
      (row.form_score != null ? scoreLabel(row.form_score) : null),
    statusScore: (row) => row.form_score ?? row.risk_score,
    answeredValue: (row) => row.answered,
    totalValue: (row) => row.total_questions,
  }),
  getHeaderMeta: (s) => {
    const c = piaRiskColor(s.risk_level);
    const fd = s.form_data || {};
    const general = fd.general || {};
    return {
      title: general.projectName || s.display_name || "PIA Submission",
      chip: s.risk_level
        ? {
            label: s.risk_level,
            color:
              s.form_score != null
                ? scoreColor(s.form_score)
                : piaRiskColor(s.risk_level),
            bg:
              s.form_score != null
                ? scoreBg(s.form_score)
                : `${piaRiskColor(s.risk_level)}18`,
          }
        : null,
      gauge:
        s.form_score != null
          ? s.form_score
          : s.risk_score
            ? Math.min(100, (s.risk_score / 25) * 100)
            : null,
      gaugeColor: c,
      gaugeLabel:
        s.form_score != null ? `${s.form_score}%` : (s.risk_score ?? "—"),
      statCards: [
        {
          tag: "VND",
          title: "Vendor",
          value: general.vendor || s.vendor || "—",
          accent: "#7dd3fc",
          icon: Building2,
        },
        {
          tag: "DEPT",
          title: "Owner Dept",
          value: general.ownerDept || "—",
          accent: "#a5b4fc",
          icon: Users,
        },
        {
          tag: "SCORE",
          title: "Weighted Score",
          value: s.form_score != null ? `${s.form_score}%` : "—",
          accent: "#6ee7b7",
          icon: BarChart3,
        },
        {
          tag: "RISK",
          title: "Risk Score",
          value: s.form_score ?? s.risk_score ?? "—",
          accent: "#fca5a5",
          icon: AlertTriangle,
        },
        {
          tag: "SENS",
          title: "Sensitive Data",
          value: fd.dataDetails?.sensitiveData || "—",
          accent: "#fcd34d",
          icon: ShieldAlert,
        },
      ],
    };
  },
  renderOverview: (s) => {
    const fd = s.form_data || {};
    const general = fd.general || {};
    const dataDetails = fd.dataDetails || {};
    const risk = fd.risk || {};
    const safeguards = fd.safeguards || {};
    return (
      <div>
        <OverviewCard title="Project & Processing">
          <OverviewRow label="Vendor" value={general.vendor || s.vendor} />
          <OverviewRow label="Project" value={general.projectName} />
          <OverviewRow
            label="Department"
            value={s.department_name || general.ownerDept}
          />
          <OverviewRow
            label="Legal Basis"
            value={(Array.isArray(general.legalBasis)
              ? general.legalBasis
              : general.legalBasis
                ? [general.legalBasis]
                : []
            )
              .map(
                (id) =>
                  ({
                    consent: "Consent",
                    contract: "Contract",
                    legal: "Legal Obligation",
                    vital: "Vital Interests",
                    legitimate: "Legitimate Interests",
                    public: "Public Task",
                  })[id] || id,
              )
              .join(", ")}
          />
          <OverviewRow label="Duration" value={general.duration} />
          <OverviewRow label="Business Purpose" value={general.bizPurpose} />
          <OverviewRow
            label="Processing Description"
            value={general.procDesc}
          />
        </OverviewCard>
        <OverviewCard title="Data & Risk">
          <OverviewRow label="Data Types" value={dataDetails.dataTypes} />
          <OverviewRow
            label="Sensitive Data"
            value={dataDetails.sensitiveData}
          />
          <OverviewRow label="Data Subjects" value={dataDetails.dataSubjects} />
          <OverviewRow label="Volume" value={dataDetails.volume} />
          <OverviewRow label="Retention" value={dataDetails.retention} />
          <OverviewRow
            label="Likelihood × Impact"
            value={
              risk.likelihood != null && risk.impact != null
                ? `${risk.likelihood} × ${risk.impact}`
                : null
            }
          />
          <OverviewRow label="Risk Category" value={risk.riskCategory} />
          <OverviewRow label="Mitigation" value={risk.mitigation} />
        </OverviewCard>
        {(safeguards.reviewerName || safeguards.reviewDate) && (
          <OverviewCard title="Review & Sign-off">
            <OverviewRow label="Reviewer" value={safeguards.reviewerName} />
            <OverviewRow label="Review Date" value={safeguards.reviewDate} />
            <OverviewRow label="Acknowledged" value={safeguards.acknowledged} />
          </OverviewCard>
        )}
      </div>
    );
  },
  renderDetails: (s) => {
    const fd = s.form_data || {};
    return (
      <div>
        {Object.entries(fd).map(([section, data]) => (
          <DataSection
            key={section}
            title={PIA_SECTION_LABELS[section] || section}
            data={data}
            labelMap={PIA_FIELD_LABELS}
          />
        ))}
      </div>
    );
  },
  buildExportSections: (s, { forExcel = false } = {}) => {
    const fd = s.form_data || {};
    const riskScore = Number(s.risk_score) || 0;
    const sections = [
      {
        heading: "Executive Summary",
        metrics: [
          {
            label: "Risk Score",
            value: `${riskScore}`,
            color: piaRiskColor(s.risk_level),
          },
          { label: "Risk Level", value: s.risk_level || "—", color: "#6366f1" },
          {
            label: "Project",
            value: fd.general?.projectName || s.display_name || "—",
            color: "#7dd3fc",
          },
          {
            label: "Vendor",
            value: fd.general?.vendor || s.vendor || "—",
            color: "#a5b4fc",
          },
        ],
        charts: riskScore
          ? [
              {
                type: "risk_gauge",
                title: "Privacy Impact Risk Score",
                value: riskScore,
                label: s.risk_level || "",
                max_value: 100,
              },
            ]
          : [],
        rows: [
          {
            label: "Project",
            value: fd.general?.projectName || s.display_name,
          },
          { label: "Vendor", value: fd.general?.vendor || s.vendor },
          { label: "Risk Level", value: s.risk_level },
          { label: "Risk Score", value: s.risk_score },
          { label: "Submitted", value: formatDate(s.created_at) },
        ].filter((r) => r.value != null && r.value !== "—" && r.value !== ""),
      },
    ];
    Object.entries(fd).forEach(([key, data]) => {
      const rows = rowsFromObject(data, PIA_FIELD_LABELS, {
        includeEmpty: forExcel,
      });
      if (rows.length) {
        sections.push({ heading: PIA_SECTION_LABELS[key] || key, rows });
      }
    });
    return sections;
  },
};

const ROPA_CONFIG = {
  apiPath: "/ropa/submissions",
  backPath: "/user/ropa",
  backLabel: "Back to ROPA",
  title: "ROPA Submissions",
  pdfPrefix: "ROPA",
  subtitle: "All submitted Records of Processing Activities",
  icon: FileText,
  computeStats: (subs, meta = {}) => {
    const total = meta.stats?.total ?? meta.total ?? subs.length;
    const complete =
      meta.stats?.complete ??
      subs.filter((s) => (s.progress_pct ?? 0) >= 100).length;
    const inProgress =
      meta.stats?.in_progress ??
      subs.filter(
        (s) => (s.progress_pct ?? 0) > 0 && (s.progress_pct ?? 0) < 100,
      ).length;
    const avgPct =
      meta.stats?.avg_progress ??
      (total
        ? Math.round(
            subs.reduce((a, s) => a + (s.progress_pct ?? 0), 0) / total,
          )
        : 0);
    return [
      {
        tag: "TOTAL",
        title: "Submissions",
        value: total,
        subtitle: "Completed ROPAs",
        accent: "#7dd3fc",
        icon: Users,
      },
      {
        tag: "FULL",
        title: "Complete",
        value: complete,
        subtitle: "100% sections filled",
        accent: "#6ee7b7",
        icon: ShieldCheck,
      },
      {
        tag: "WIP",
        title: "In Progress",
        value: inProgress,
        subtitle: "Partially completed",
        accent: "#fcd34d",
        icon: BarChart3,
      },
      {
        tag: "AVG",
        title: "Avg Progress",
        value: `${avgPct}%`,
        subtitle: "Across submissions",
        accent: "#a5b4fc",
        icon: BarChart3,
      },
    ];
  },
  searchFilter: (r, q) =>
    (r.display_name || "").toLowerCase().includes(q) ||
    (r.org_name || "").toLowerCase().includes(q) ||
    (r.activity_name || "").toLowerCase().includes(q) ||
    (r.department_name || "").toLowerCase().includes(q),
  columns: ROPA_GAP_COLUMNS,
  getHeaderMeta: (s) => {
    const pct = s.progress_pct ?? 0;
    const c = scoreColor(pct);
    const org = s.form_data?.org || {};
    const activity = s.form_data?.activity || {};
    return {
      title: org.name || s.org_name || s.display_name || "ROPA Submission",
      chip: {
        label: scoreLabel(pct),
        color: c,
        bg: scoreBg(pct),
      },
      gauge: pct,
      gaugeColor: c,
      statCards: [
        {
          tag: "SEC",
          title: "Sections Filled",
          value: `${s.sections_filled ?? 0}/${s.sections_total ?? 12}`,
          accent: "#7dd3fc",
          icon: ClipboardList,
        },
        {
          tag: "ACT",
          title: "Activity",
          value: activity.name || s.activity_name || "—",
          accent: "#a5b4fc",
          icon: FileText,
        },
        {
          tag: "ROLE",
          title: "Org Role",
          value: org.role || "—",
          accent: "#fcd34d",
          icon: Building2,
        },
        {
          tag: "STS",
          title: "Status",
          value: s.status || "—",
          accent: "#6ee7b7",
          icon: ShieldCheck,
        },
      ],
    };
  },
  renderOverview: (s) => {
    const fd = s.form_data || {};
    const org = fd.org || {};
    const activity = fd.activity || {};
    const sectionProgress = {};
    Object.keys(ROPA_SECTION_LABELS).forEach((sid) => {
      const sec = fd[sid];
      if (!sec) {
        sectionProgress[sid] = 0;
        return;
      }
      if (Array.isArray(sec.rows)) {
        const filled = sec.rows.filter((row) =>
          Object.values(row).some((v) => v != null && v !== ""),
        ).length;
        sectionProgress[sid] = sec.rows.length
          ? Math.round((filled / sec.rows.length) * 100)
          : 0;
        return;
      }
      const vals = Object.values(sec);
      const filled = vals.filter((v) => {
        if (Array.isArray(v)) return v.length > 0;
        return v != null && v !== "";
      }).length;
      sectionProgress[sid] = vals.length
        ? Math.round((filled / vals.length) * 100)
        : 0;
    });
    return (
      <div>
        <OverviewCard title="Organisation">
          <OverviewRow label="Name" value={org.name || s.org_name} />
          <OverviewRow label="Role" value={org.role} />
          <OverviewRow label="Contact Email" value={org.email} />
          <OverviewRow label="Contact Phone" value={org.phone} />
          <OverviewRow label="Grievance Officer" value={org.grievance} />
          <OverviewRow label="SDF Status" value={org.sdf} />
        </OverviewCard>
        <OverviewCard title="Processing Activity">
          <OverviewRow
            label="Activity"
            value={activity.name || s.activity_name}
          />
          <OverviewRow
            label="Department"
            value={s.department_name || activity.dept}
          />
          <OverviewRow label="Purpose" value={activity.purpose} />
          <OverviewRow label="Description" value={activity.desc} />
        </OverviewCard>
        <OverviewCard title="Section Completion">
          <SectionScoresPanel
            scores={sectionProgress}
            titleFn={(k) => ROPA_SECTION_LABELS[k] || k}
          />
        </OverviewCard>
      </div>
    );
  },
  renderDetails: (s) => {
    const fd = s.form_data || {};
    return (
      <div>
        {Object.entries(fd).map(([section, data]) => {
          if (data?.rows) {
            const cols = ROPA_ROW_COLUMNS[section];
            return (
              <div
                key={section}
                className="mb-3 rounded-xl border border-indigo-500/10 overflow-hidden"
              >
                <div className="px-3.5 py-2.5 bg-slate-50 text-xs font-bold text-slate-900">
                  {ROPA_SECTION_LABELS[section] || section}
                </div>
                <div className="px-3.5 py-3">
                  <RowsDetailTable columns={cols || []} rows={data.rows} />
                  {!cols?.length && (
                    <p className="text-xs text-slate-500 m-0">
                      {data.rows.length} row(s) recorded
                    </p>
                  )}
                </div>
              </div>
            );
          }
          return (
            <DataSection
              key={section}
              title={ROPA_SECTION_LABELS[section] || section}
              data={data}
              labelMap={ROPA_FIELD_LABELS}
            />
          );
        })}
      </div>
    );
  },
  buildExportSections: (s, { forExcel = false } = {}) => {
    const fd = s.form_data || {};
    const progress = Number(s.progress_pct) || 0;
    const filled = Number(s.sections_filled) || 0;
    const total = Number(s.sections_total) || 12;
    const sections = [
      {
        heading: "Executive Summary",
        metrics: [
          {
            label: "Progress",
            value: `${progress}%`,
            color: scoreColor(progress),
          },
          {
            label: "Sections Filled",
            value: `${filled}/${total}`,
            color: "#6366f1",
          },
          {
            label: "Organisation",
            value: fd.org?.name || s.org_name || "—",
            color: "#7dd3fc",
          },
          {
            label: "Activity",
            value: fd.activity?.name || s.activity_name || "—",
            color: "#a5b4fc",
          },
        ],
        charts: [
          {
            type: "donut",
            title: "ROPA Completion",
            value: progress,
            suffix: "%",
            label:
              progress >= 100
                ? "Complete"
                : progress >= 50
                  ? "In Progress"
                  : "Started",
          },
          {
            type: "pie",
            title: "Section Coverage",
            labels: ["Completed", "Remaining"],
            values: [filled, Math.max(total - filled, 0)],
          },
        ],
        rows: [
          { label: "Organisation", value: fd.org?.name || s.org_name },
          { label: "Activity", value: fd.activity?.name || s.activity_name },
          { label: "Progress", value: `${progress}%` },
          { label: "Sections", value: `${filled}/${total}` },
          {
            label: "Submitted",
            value: formatDate(s.pushed_at || s.created_at),
          },
        ],
      },
    ];
    Object.entries(fd).forEach(([section, data]) => {
      if (data?.rows?.length) {
        const cols = ROPA_ROW_COLUMNS[section] || [];
        sections.push({
          heading: ROPA_SECTION_LABELS[section] || section,
          table: cols.length
            ? {
                headers: cols.map((c) => c.label),
                rows: data.rows.map((row) =>
                  cols.map((c) => flattenValue(row[c.key])),
                ),
              }
            : {
                headers: ["Row", "Details"],
                rows: data.rows.map((row, i) => [
                  String(i + 1),
                  flattenValue(row),
                ]),
              },
        });
      } else {
        const rows = rowsFromObject(data, ROPA_FIELD_LABELS, {
          includeEmpty: forExcel,
        });
        if (rows.length) {
          sections.push({
            heading: ROPA_SECTION_LABELS[section] || section,
            rows,
          });
        }
      }
    });
    return sections;
  },
};

function DataTypeCell({ value }) {
  const [modalOpen, setModalOpen] = useState(false);
  if (!value) return <span className="text-slate-400">—</span>;

  const tags = value.split(",").map((t) => t.trim()).filter(Boolean);
  if (tags.length === 0) return <span className="text-slate-400">—</span>;

  const limit = 2;
  const hasMore = tags.length > limit;
  const visibleTags = tags.slice(0, limit);

  return (
    <>
      <div className="flex flex-col gap-1 max-w-[200px]">
        <div className="flex flex-wrap gap-1">
          {visibleTags.map((label, idx) => (
            <span
              key={idx}
              className="inline-flex items-center text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-medium border border-slate-200"
            >
              {label}
            </span>
          ))}
        </div>
        {hasMore && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setModalOpen(true);
            }}
            className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold text-left self-start mt-0.5"
          >
            {`+${tags.length - limit} more`}
          </button>
        )}
      </div>

      {modalOpen && (
        <Modal
          open={modalOpen}
          onClose={(e) => {
            if (e) e.stopPropagation();
            setModalOpen(false);
          }}
          title="All Data Types"
          size="sm"
        >
          <div className="flex flex-wrap gap-2 max-h-[300px] overflow-y-auto">
            {tags.map((label, idx) => (
              <span
                key={idx}
                className="inline-flex items-center text-[11px] bg-slate-100 text-slate-700 px-2.5 py-1 rounded font-medium border border-slate-200 shadow-sm"
              >
                {label}
              </span>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(false);
              }}
              className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

const ROPA_AUTOMATED_CONFIG = {
  ...ROPA_CONFIG,
  apiPath: "/ropa/submissions/automated",
  activityLogsPath: "/ropa/submissions/automated",
  showActivityLog: true,
  backPath: "/user/ropa",
  backLabel: "Back to ROPA",
  title: "Automated ROPA",
  subtitle:
    "Entries auto-pushed from connected DSPM systems (e.g. DataForesight)",
  searchFilter: (r, q) =>
    (r.display_name || "").toLowerCase().includes(q) ||
    (r.org_name || "").toLowerCase().includes(q) ||
    (r.activity_name || "").toLowerCase().includes(q) ||
    (r.source || "").toLowerCase().includes(q) ||
    (r.entry_name || "").toLowerCase().includes(q) ||
    (r.department_name || "").toLowerCase().includes(q) ||
    (r.database_name || "").toLowerCase().includes(q) ||
    (r.platform || "").toLowerCase().includes(q) ||
    (r.database_label || "").toLowerCase().includes(q) ||
    (r.form_data?.personaldata?.dataType || "").toLowerCase().includes(q) ||
    (r.form_data?.lifecycle?.location || "").toLowerCase().includes(q),
  columns: [
    {
      key: "display_name",
      label: "Host",
      render: (row) => (
        <div className="font-semibold text-slate-900">
          {row.database_label || row.org_name || row.display_name || "—"}
        </div>
      ),
    },
    {
      key: "department_name",
      label: "Department",
      render: (row) => (
        <span
          className={`text-xs font-medium ${
            row.department_conflict ? "text-amber-700" : "text-slate-700"
          }`}
          title={
            row.department_conflict
              ? "Multiple departments mapped to this database in Asset Tracker"
              : undefined
          }
        >
          {row.department_name || "—"}
        </span>
      ),
    },
    {
      key: "location",
      label: "Location",
      render: (row) => (
        <span className="text-slate-700 text-xs font-medium">
          {row.form_data?.lifecycle?.location || "—"}
        </span>
      ),
    },
    {
      key: "data_owner",
      label: "Data Owner",
      render: (row) => (
        <span className="text-slate-700 text-xs font-medium">
          {row.form_data?.activity?.dataOwner || "—"}
        </span>
      ),
    },
    {
      key: "consent_type",
      label: "Consent",
      render: (row) => (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          {row.form_data?.legal?.consentType || "—"}
        </span>
      ),
    },
    {
      key: "data_type",
      label: "Data Type",
      render: (row) => (
        <DataTypeCell value={row.form_data?.personaldata?.dataType} />
      ),
    },
    {
      key: "source",
      label: "Source",
      render: (row) => (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">
          {row.source || "DataForesight"}
        </span>
      ),
    },
    {
      key: "review_status",
      label: "Review Status",
      render: (row) => {
        const rs = (row.review_status || "").toLowerCase();
        const approved =
          rs === "approved" || rs === "approve" || rs === "submitted";
        return (
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
              approved
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-amber-50 text-amber-700 border border-amber-200"
            }`}
          >
            {row.review_status || "pushed"}
          </span>
        );
      },
    },
    ROPA_GAP_COLUMNS.find((c) => c.key === "form_score"),
    ROPA_GAP_COLUMNS.find((c) => c.key === "status"),
    {
      key: "pushed_at",
      label: "Pushed At",
      render: (row) => (
        <span className="text-slate-600 text-xs flex items-center gap-1.5">
          <Calendar size={12} />{" "}
          {formatDate(row.pushed_at || row.updated_at || row.created_at)}
        </span>
      ),
    },
  ],
};

const TPRM_CONFIG = {
  apiPath: "/tprm/submissions",
  questionnairePath: "/tprm/questionnaire",
  attachmentsListPath: "/tprm/attachments",
  backPath: "/user/tprm",
  backLabel: "Back to TPRM",
  title: "TPRM Submissions",
  pdfPrefix: "TPRM",
  subtitle: "All submitted third-party risk assessments",
  icon: ShieldCheck,
  computeStats: (subs, meta = {}) => {
    const total = meta.stats?.total ?? meta.total ?? subs.length;
    const compliant =
      meta.stats?.compliant ??
      subs.filter((s) => (s.overall_score ?? 0) >= 75).length;
    const critical =
      meta.stats?.critical ??
      subs.filter((s) => (s.overall_score ?? 0) < 50).length;
    const avg =
      meta.stats?.avg ??
      (total
        ? Math.round(
            subs.reduce((a, s) => a + (s.overall_score ?? 0), 0) / total,
          )
        : 0);
    return [
      {
        tag: "TOTAL",
        title: "Submissions",
        value: total,
        subtitle: "Completed assessments",
        accent: "#7dd3fc",
        icon: Users,
      },
      {
        tag: "AVG",
        title: "Average Score",
        value: `${avg}%`,
        subtitle: "Across all vendors",
        accent: "#a5b4fc",
        icon: BarChart3,
      },
      {
        tag: "PASS",
        title: "Compliant",
        value: compliant,
        subtitle: "Score ≥ 75%",
        accent: "#6ee7b7",
        icon: ShieldCheck,
      },
      {
        tag: "RISK",
        title: "Critical",
        value: critical,
        subtitle: "Score below 50%",
        accent: "#fca5a5",
        icon: ShieldAlert,
      },
    ];
  },
  searchFilter: (r, q) =>
    (r.vendor_name || "").toLowerCase().includes(q) ||
    (r.display_name || "").toLowerCase().includes(q) ||
    (r.status || "").toLowerCase().includes(q) ||
    (r.department_name || "").toLowerCase().includes(q) ||
    String(r.overall_score ?? "").includes(q),
  columns: buildGapSubmissionColumns({
    orgTitle: (row) => row.vendor_name || row.display_name || "TPRM Assessment",
    orgSubtitle: () => null,
    scoreKey: "overall_score",
    scoreValue: (row) => row.overall_score,
    statusLabel: (row) => scoreLabel(row.overall_score ?? 0),
    statusScore: (row) => row.overall_score,
    answeredValue: (row) => row.answered,
    totalValue: (row) => row.total_questions,
  }),
  getHeaderMeta: (s, { questions = [] } = {}) => {
    const sc = s.overall_score ?? 0;
    const c = scoreColor(sc);
    const vendor = tprmVendorName(s, questions);
    const totalQ = questions.length || null;
    return {
      title: vendor,
      chip: { label: scoreLabel(sc), color: c, bg: scoreBg(sc) },
      gauge: sc,
      gaugeColor: c,
      statCards: [
        {
          tag: "ANS",
          title: "Answered",
          value: totalQ ? `${s.answered ?? 0}/${totalQ}` : (s.answered ?? "—"),
          accent: "#7dd3fc",
          icon: ClipboardList,
        },
        {
          tag: "SEC",
          title: "Sections",
          value: Object.keys(s.section_scores || {}).length,
          accent: "#a5b4fc",
          icon: BarChart3,
        },
        {
          tag: "STS",
          title: "Status",
          value: s.status || "—",
          accent: "#6ee7b7",
          icon: ShieldCheck,
        },
        {
          tag: "DATE",
          title: "Submitted",
          value: formatDate(s.created_at),
          accent: "#fcd34d",
          icon: Calendar,
        },
      ],
    };
  },
  renderOverview: (s, { questions = [] } = {}) => {
    const vendor = tprmVendorName(s, questions);
    return (
      <div>
        <OverviewCard title="Assessment Summary">
          <OverviewRow label="Vendor / Organisation" value={vendor} />
          <OverviewRow label="Department" value={s.department_name} />
          <OverviewRow
            label="Overall Score"
            value={`${s.overall_score ?? 0}%`}
          />
          <OverviewRow
            label="Compliance"
            value={scoreLabel(s.overall_score ?? 0)}
          />
          <OverviewRow label="Questions Answered" value={s.answered} />
          <OverviewRow label="Submitted" value={formatDate(s.created_at)} />
        </OverviewCard>
        <OverviewCard title="Section Scores">
          <SectionScoresPanel
            scores={s.section_scores}
            titleFn={tprmSectionTitle}
          />
        </OverviewCard>
      </div>
    );
  },
  renderDetails: (
    s,
    {
      questions = [],
      questionsLoading = false,
      attachmentsByQuestion = {},
    } = {},
  ) => (
    <QuestionnaireDetails
      submission={s}
      questions={questions}
      loading={questionsLoading}
      attachmentsByQuestion={attachmentsByQuestion}
    />
  ),
  enrichSubmission: (s, { questions = [] } = {}) => ({
    ...s,
    vendor_name: tprmVendorName(s, questions),
    display_name: tprmVendorName(s, questions),
    total_questions: questions.length || s.total_questions || null,
  }),
  buildExportSections: (s, { questions = [], forExcel = false } = {}) => {
    const displayQuestions = s.questions_snapshot?.length
      ? s.questions_snapshot
      : questions;
    const getAnswer = buildAnswerLookup(displayQuestions, s.answers || {});
    const vendor = tprmVendorName(s, questions);
    const overall = Number(s.overall_score) || 0;
    const scoreKeys = Object.keys(s.section_scores || {});
    const sortedCats = sortTprmCategories(scoreKeys);
    const sections = [
      {
        heading: "Executive Summary",
        metrics: [
          {
            label: "Overall Score",
            value: `${overall}%`,
            color: scoreColor(overall),
          },
          { label: "Compliance", value: scoreLabel(overall), color: "#6366f1" },
          { label: "Vendor", value: vendor || "—", color: "#7dd3fc" },
          {
            label: "Answered",
            value: String(s.answered ?? "—"),
            color: "#10b981",
          },
        ],
        charts: [
          {
            type: "donut",
            title: "Overall Compliance",
            value: overall,
            suffix: "%",
            label: scoreLabel(overall),
          },
          ...(sortedCats.length
            ? [
                {
                  type: "bar_horizontal",
                  title: "Scores by Category",
                  labels: sortedCats.map((cat) => tprmSectionTitle(cat)),
                  values: sortedCats.map(
                    (cat) => Number(s.section_scores[cat]) || 0,
                  ),
                  suffix: "%",
                  max_value: 100,
                },
              ]
            : []),
        ],
        rows: [
          { label: "Vendor / Organisation", value: vendor },
          { label: "Overall Score", value: `${overall}%` },
          { label: "Compliance", value: scoreLabel(overall) },
          { label: "Questions Answered", value: s.answered },
          { label: "Submitted", value: formatDate(s.created_at) },
        ],
      },
    ];
    if (scoreKeys.length) {
      sections.push({
        heading: "Section Scores",
        rows: sortedCats.map((cat) => ({
          label: tprmSectionTitle(cat),
          value: `${s.section_scores[cat]}%`,
        })),
      });
    }
    const grouped = {};
    [...displayQuestions]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((q) => {
        const cat = q.category || "general";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(q);
      });

    if (Object.keys(grouped).length) {
      const sortedCats = sortTprmCategories(Object.keys(grouped));
      if (forExcel) {
        const allRows = sortedCats.flatMap((cat) =>
          grouped[cat].map((q) => ({
            section: tprmSectionTitle(cat),
            question_id: String(q.id),
            question: q.text,
            answer: flattenValue(getAnswer(q.id) ?? "—"),
          })),
        );
        sections.push({
          heading: "All Questions and Answers",
          table: {
            headers: ["Section", "Question ID", "Question", "Answer"],
            rows: allRows.map((r) => [
              r.section,
              r.question_id,
              r.question,
              r.answer,
            ]),
          },
          qa: allRows.map((r) => ({
            section: r.section,
            question_id: r.question_id,
            question: r.question,
            answer: r.answer,
          })),
        });
      }
      sortedCats.forEach((cat) => {
        sections.push({
          heading: tprmSectionTitle(cat),
          qa: grouped[cat].map((q) => ({
            section: tprmSectionTitle(cat),
            question_id: String(q.id),
            question: q.text,
            answer: flattenValue(getAnswer(q.id) ?? (forExcel ? "—" : "")),
          })),
        });
      });
    } else if (forExcel && s.answers && Object.keys(s.answers).length) {
      const qMap = buildQuestionMap(displayQuestions);
      sections.push({
        heading: "All Responses",
        qa: Object.entries(s.answers)
          .sort(([a], [b]) => sortAnswerKeys(a, b))
          .map(([qid, ans]) => {
            const q = qMap.get(String(qid));
            return {
              section: tprmSectionTitle(q?.category || "other"),
              question_id: String(qid),
              question: q?.text || `Question ${qid}`,
              answer: flattenValue(ans),
            };
          }),
      });
    }
    return sections;
  },
};

const SUBMITTER_COLUMN = {
  key: "submitted_by_name",
  label: "Submitted By",
  render: (row) => (
    <span className="text-xs text-slate-600">
      {row.submitted_by_name || "—"}
    </span>
  ),
};

export function withAdminMonitorConfig(baseConfig) {
  const cols = [...baseConfig.columns];
  if (cols.some((c) => c.key === "submitted_by_name")) {
    return { ...baseConfig, columns: cols, responsesTabLabel: "All Responses" };
  }
  const deptIdx = cols.findIndex((c) => c.key === "department_name");
  if (deptIdx >= 0) cols.splice(deptIdx + 1, 0, SUBMITTER_COLUMN);
  else cols.unshift(SUBMITTER_COLUMN);
  return { ...baseConfig, columns: cols, responsesTabLabel: "All Responses" };
}

export {
  FormSubmissionsPage,
  PIA_CONFIG,
  ROPA_CONFIG,
  ROPA_AUTOMATED_CONFIG,
  TPRM_CONFIG,
};

export function PIASubmissions() {
  return <FormSubmissionsPage config={PIA_CONFIG} />;
}

export function ROPASubmissions() {
  return <FormSubmissionsPage config={ROPA_CONFIG} />;
}

export function ROPAAutomatedSubmissions() {
  return <FormSubmissionsPage config={ROPA_AUTOMATED_CONFIG} />;
}

export function TPRMSubmissions() {
  return <FormSubmissionsPage config={TPRM_CONFIG} />;
}
